// src/components/common/ImportProductsButton.tsx
import React, { useState } from 'react';
import { Button, Modal, Upload, Select, message, Space, Typography } from 'antd';
import { ImportOutlined, InboxOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { dbAPI, STORE_NAMES, type StoreName } from '../../utils/db';
import { mapToSparkPlug, mapToSensor, mapToIgnitionCoil, mapToSteeringClock, mapToWires } from '../../utils/importMappers';

const { Dragger } = Upload;
const { Text } = Typography;

const categoryOptions = [
  { label: 'Катушки Зажигания',     value: STORE_NAMES.coils          },
  { label: 'Свечи Зажигания',       value: STORE_NAMES.sparkPlugs     },
  { label: 'Наконечники и Проводки', value: STORE_NAMES.wires          },
  { label: 'Датчики',               value: STORE_NAMES.sensors        },
  { label: 'Рулевые Шлейфы',        value: STORE_NAMES.steeringClocks },
];

// ─── per-store field mappers ──────────────────────────────────────────────────

type RawRow = Record<string, unknown>;

const MAPPERS: Partial<Record<StoreName, (row: RawRow) => unknown>> = {
  [STORE_NAMES.sparkPlugs]:     mapToSparkPlug,
  [STORE_NAMES.sensors]:        mapToSensor,
  [STORE_NAMES.coils]:          mapToIgnitionCoil,
  [STORE_NAMES.steeringClocks]: mapToSteeringClock,
  [STORE_NAMES.wires]:          mapToWires,
};

function applyMapper(store: StoreName, rows: RawRow[]): unknown[] {
  const mapper = MAPPERS[store];
  if (!mapper) {
    // Default: keep raw row, ensure id exists.
    return rows.map(row => ({ id: row.id ?? crypto.randomUUID(), ...row }));
  }
  return rows.map(mapper);
}

// ─── encoding-aware workbook reader ──────────────────────────────────────────

// phpMyAdmin exports UTF-8 CSV; old XLS files may be Windows-1251.
// SheetJS codepage option is for binary XLS only — applying it to a UTF-8 CSV
// corrupts every multi-byte (Cyrillic/Armenian) character.
function readWorkbook(data: Uint8Array, fileName: string): XLSX.WorkBook {
  const ext = fileName.split('.').pop()?.toLowerCase();

  if (ext === 'csv') {
    // Decode as UTF-8 text, let SheetJS parse the CSV string.
    const text = new TextDecoder('utf-8').decode(data);
    return XLSX.read(text, { type: 'string' });
  }

  if (ext === 'xls') {
    // Legacy binary Excel — may be Windows-1251 on Russian systems.
    return XLSX.read(data, { type: 'array', codepage: 1251 });
  }

  // Modern .xlsx — always UTF-8 internally; no codepage needed.
  return XLSX.read(data, { type: 'array' });
}

// ─── component ───────────────────────────────────────────────────────────────

export const ImportProductsButton: React.FC = () => {
  const [isModalOpen,   setIsModalOpen]   = useState(false);
  const [selectedStore, setSelectedStore] = useState<StoreName | null>(null);
  const [isLoading,     setIsLoading]     = useState(false);

  const handleImport = async (file: File) => {
    if (!selectedStore) {
      message.error('Сначала выберите категорию для импорта!');
      return false;
    }

    setIsLoading(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const raw      = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = readWorkbook(raw, file.name);

        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData  = XLSX.utils.sheet_to_json<RawRow>(worksheet);

        if (jsonData.length === 0) {
          message.warning('Файл пуст или формат не поддерживается.');
          setIsLoading(false);
          return;
        }

        const itemsToSave = applyMapper(selectedStore, jsonData);

        // Full replacement: clear stale records before importing.
        await dbAPI.clear(selectedStore);

        for (const item of itemsToSave) {
          await dbAPI.put(selectedStore, item);
        }

        message.success(`Импортировано ${itemsToSave.length} записей.`);
        setIsModalOpen(false);
        setTimeout(() => window.location.reload(), 800);

      } catch (error) {
        console.error('Import error:', error);
        message.error('Ошибка при чтении файла. Проверьте формат и кодировку.');
      } finally {
        setIsLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
    return false;
  };

  return (
    <>
      <Button icon={<ImportOutlined />} onClick={() => setIsModalOpen(true)}>
        Импорт
      </Button>

      <Modal
        title="Импорт товаров из Excel / CSV"
        open={isModalOpen}
        onCancel={() => !isLoading && setIsModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>1. Выберите категорию товаров:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Выберите категорию..."
              options={categoryOptions}
              value={selectedStore}
              onChange={setSelectedStore}
              disabled={isLoading}
            />
          </div>

          <div>
            <Text strong>2. Загрузите файл (.xlsx, .xls, .csv):</Text>
            <div style={{ marginTop: 8 }}>
              <Dragger
                accept=".xlsx, .xls, .csv"
                multiple={false}
                beforeUpload={handleImport}
                showUploadList={false}
                disabled={!selectedStore || isLoading}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Нажмите или перетащите файл сюда
                </p>
                <p className="ant-upload-hint">
                  {selectedStore
                    ? 'Импорт заменит все существующие записи в этой категории.'
                    : 'Сначала выберите категорию выше!'}
                </p>
              </Dragger>
            </div>
          </div>
        </Space>
      </Modal>
    </>
  );
};
